import { DataLayerItemType, DataSourceType } from '../../models/api/dataLayer.model';

// Supported file extensions for filtering - these match the available icons
export const SUPPORTED_FILE_EXTENSIONS = [
  'csv',
  'docx',
  'ipynb',
  'jpg',
  'js',
  'json',
  'loop',
  'md',
  'pdf',
  'png',
  'py',
  'txt',
  'xl',
  'xlsx',
  'yml',
  'pptx',
] as const;

export type SupportedFileExtension = (typeof SUPPORTED_FILE_EXTENSIONS)[number];

export function getFileExtensionIcon(extension: string): string {
  const upperExtension = extension.toUpperCase();
  return `assets/images/file-types/${upperExtension}.svg`;
}

export function getFileExtensionDisplayName(extension: string): string {
  return extension.toUpperCase();
}

export function getFileExtension(name?: string): string | null {
  if (!name) return null;
  const lastDotIndex = name.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === name.length - 1) return null;

  let extension = name.substring(lastDotIndex + 1).trim();

  const underscoreIndex = extension.indexOf('_');
  if (underscoreIndex > 0) {
    extension = extension.substring(0, underscoreIndex);
  }

  return extension;
}

export function getDataLayerItemIcon(
  itemId: string,
  resourceName: string,
  dataType: DataLayerItemType,
  fileExtension?: string | null
): string {
  if (
    dataType === DataLayerItemType.file ||
    dataType === DataLayerItemType.document ||
    dataType === DataLayerItemType.spreadsheet ||
    dataType === DataLayerItemType.presentation
  ) {
    if (fileExtension) {
      return `assets/images/file-types/${fileExtension.toUpperCase()}.svg`;
    }

    const extFromId = getFileExtension(itemId);
    if (extFromId) {
      return `assets/images/file-types/${extFromId.toUpperCase()}.svg`;
    }

    const extFromName = getFileExtension(resourceName);
    if (extFromName) {
      return `assets/images/file-types/${extFromName.toUpperCase()}.svg`;
    }
  }

  return getDataLayerItemTypeIcon(dataType);
}

export function getDataLayerItemTypeIcon(dataType: DataLayerItemType): string {
  switch (dataType) {
    case DataLayerItemType.file:
      return 'assets/images/common/file.svg';
    case DataLayerItemType.database:
      return 'assets/images/data-lens/database.svg';
    case DataLayerItemType.dataSource:
      return 'assets/images/data-lens/data-source.svg';
    case DataLayerItemType.knowledgeBase:
      return 'assets/images/data-lens/knowledge-base.svg';
    case DataLayerItemType.document:
      return 'assets/images/data-lens/document.svg';
    case DataLayerItemType.spreadsheet:
      return 'assets/images/data-lens/spreadsheet.svg';
    case DataLayerItemType.presentation:
      return 'assets/images/data-lens/presentation.svg';
    case DataLayerItemType.sharepoint:
      return 'assets/images/data-lens/sharepoint.svg';
    case DataLayerItemType.email:
      return 'assets/images/data-lens/email.svg';
    case DataLayerItemType.website:
      return 'assets/images/common/globe.svg';
    case DataLayerItemType.messages:
      return 'assets/images/data-lens/messages-square.svg';
    default:
      return 'assets/images/common/file.svg';
  }
}

export function getDataSourceTypeIcon(sourceType: DataSourceType): string {
  switch (sourceType) {
    case DataSourceType.fileSystem:
      return 'assets/images/data-lens/file-system.svg';
    case DataSourceType.sharePoint:
      return 'assets/images/data-lens/sharepoint.svg';
    case DataSourceType.oneDrive:
      return 'assets/images/data-lens/onedrive.svg';
    case DataSourceType.teams:
      return 'assets/images/data-lens/teams.svg';
    case DataSourceType.email:
      return 'assets/images/data-lens/email.svg';
    case DataSourceType.database:
      return 'assets/images/data-lens/database.svg';
    case DataSourceType.api:
      return 'assets/images/data-lens/api.svg';
    case DataSourceType.vector:
      return 'assets/images/data-lens/vector.svg';
    case DataSourceType.embedding:
      return 'assets/images/data-lens/embedding.svg';
    default:
      return 'assets/images/data-lens/other.svg';
  }
}

export function getDataLayerItemTypeDisplayName(dataType: DataLayerItemType): string {
  switch (dataType) {
    case DataLayerItemType.file:
      return 'File';
    case DataLayerItemType.document:
      return 'Document';
    case DataLayerItemType.email:
      return 'Email';
    case DataLayerItemType.website:
      return 'Website';
    case DataLayerItemType.messages:
      return 'Messages';
    case DataLayerItemType.unknown:
      return 'Unknown';
    default:
      return 'Unknown';
  }
}
